import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/data/mock";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/about", label: "About" },
  { to: "/testimonials", label: "Reviews" },
  { to: "/contact", label: "Contact" }
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all ${
        scrolled ? "glass-nav border-b border-brand-line/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={COMPANY.logo}
            alt="NBB — Neighbours Brick & Build logo"
            className="w-10 h-10 rounded-full object-cover ring-1 ring-brand-line shadow-sm"
          />
          <span className="font-display tracking-tight text-brand-ink leading-none">
            <span className="block text-[15px] font-semibold">Neighbours</span>
            <span className="block text-[11px] text-brand-inkSoft tracking-[0.18em] uppercase">
              Brick &amp; Build
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive
                    ? "bg-brand-bgAlt text-brand-ink"
                    : "text-brand-inkSoft hover:text-brand-ink hover:bg-brand-bgAlt/60"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
            className="text-sm text-brand-inkSoft hover:text-brand-ink"
            data-testid="navbar-phone"
          >
            {COMPANY.phone}
          </a>
          <Button
            asChild
            data-testid="navbar-cta-quote"
            className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaDark text-white px-5 h-10 transition-transform hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(192,86,64,0.25)]"
          >
            <Link to="/contact">
              Get a quote <ArrowUpRight className="ml-1 w-4 h-4" />
            </Link>
          </Button>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="navbar-mobile-toggle"
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-brand-line bg-white/70"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-line bg-brand-bg" data-testid="navbar-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base ${
                    isActive ? "bg-brand-bgAlt text-brand-ink" : "text-brand-inkSoft"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Button
              asChild
              data-testid="mobile-cta-quote"
              className="mt-3 rounded-full bg-brand-terracotta hover:bg-brand-terracottaDark text-white"
            >
              <Link to="/contact">Get a quote</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
